import { BreadCrumbs, Filter, HomeIcon, Input, MultiSelect } from '@isdd/idsk-ui-kit/index'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RELATION_TYPE } from '@isdd/metais-common/api'
import { CiLazySelect } from '@isdd/metais-common/components/ci-lazy-select/CiLazySelect'
import { JOIN_OPERATOR, OPERATOR_SEPARATOR, RELATION_FILTER_PREFIX } from '@isdd/metais-common/constants'
import { DynamicFilterAttributes, ExtendedAttribute } from '@isdd/metais-common/components/dynamicFilterAttributes/DynamicFilterAttributes'
import { MetainformationColumns } from '@isdd/metais-common/componentHelpers/ci/getCiDefaultMetaAttributes'
import { EnumType } from '@isdd/metais-common/api/generated/enums-repo-swagger'
import { AttributeProfile } from '@isdd/metais-common/api/generated/types-repo-swagger'
import { useSearchParams } from 'react-router-dom'
import { RowSelection } from '@isdd/metais-common/hooks/useRowSelection'
import { Languages } from '@isdd/metais-common/localization/languages'
import { join } from 'lodash'

import { CIFilterData } from '@/pages/ci/[entityName]/entity'
import { getCiHowToBreadCrumb, useGetEntityParamsFromUrl } from '@/componentHelpers/ci'
import { MainContentWrapper } from '@/components/MainContentWrapper'
import { ListWrapper } from '@/components/list-wrapper/ListWrapper'
import { CiListWizard } from '@/components/wizards/CiListWizard'
import { CiListContainer } from '@/components/containers/CiListContainer'

const GestorProjektRelType = `${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.PO_asociuje_Projekt}` as 'GestorProjektRelType'
const ProgramProjektRelType = `${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.Program_financuje_Projekt}` as 'ProgramProjektRelType'

export interface ProjektFilterData extends CIFilterData {
    [GestorProjektRelType]?: string
    [ProgramProjektRelType]?: string
    'EA_Profil_Projekt_typ_investicie--eq'?: string
}
export interface ProjektFilterProps {
    defaultFilterValues: ProjektFilterData
    entityName: string
    codePrefix: string
    constraintsData: (EnumType | undefined)[] | undefined
    attributes: ExtendedAttribute[] | undefined
    attributeProfiles: AttributeProfile[] | undefined
    rowSelection: RowSelection<object>
}

export const ProjektFilter: React.FC<ProjektFilterProps> = ({
    defaultFilterValues,
    attributes,
    attributeProfiles,
    constraintsData,
    codePrefix,
    entityName,
    rowSelection,
}) => {
    const { t, i18n } = useTranslation()
    const [urlParams] = useSearchParams()
    const gestorRelationFromUrl = urlParams.get(GestorProjektRelType)
    const programRelationFromUrl = urlParams.get(ProgramProjektRelType)
    const investmentTypeFromUrl = urlParams.get('EA_Profil_Projekt_typ_investicie--eq')
    const [investmentType, setInvestmentType] = useState(
        investmentTypeFromUrl
            ? investmentTypeFromUrl.includes(JOIN_OPERATOR)
                ? investmentTypeFromUrl.split(JOIN_OPERATOR)
                : [investmentTypeFromUrl]
            : [defaultFilterValues['EA_Profil_Projekt_typ_investicie--eq'] as string],
    )

    const investmentTypeConstraints = constraintsData?.find((c) => c?.code === 'TYP_INVESTICIE')
    const optionsForSelects =
        investmentTypeConstraints?.enumItems
            ?.filter((item) => item.valid)
            .map((item) => {
                const qualifier =
                    i18n.language === Languages.SLOVAK
                        ? item.qualifierName
                            ? `(${item.qualifierName})`
                            : ''
                        : item.qualifierNameEng
                        ? `(${item.qualifierNameEng})`
                        : ''
                return {
                    label: item.description ? `${item.description} ${qualifier}` : `${item.value} ${qualifier}`,
                    value: `${item.code}`,
                }
            }) ?? []

    return (
        <Filter<ProjektFilterData, object>
            rowSelection={rowSelection}
            defaultFilterValues={defaultFilterValues}
            form={({ register, filter, setValue, isOpen }) => {
                return (
                    <>
                        <CiLazySelect
                            ciType={'PO'}
                            label={t(`${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.PO_asociuje_Projekt}`)}
                            name={GestorProjektRelType}
                            setValue={setValue}
                            defaultValue={
                                gestorRelationFromUrl
                                    ? gestorRelationFromUrl.includes(JOIN_OPERATOR)
                                        ? gestorRelationFromUrl.split(JOIN_OPERATOR)
                                        : gestorRelationFromUrl
                                    : defaultFilterValues[GestorProjektRelType] ?? ''
                            }
                            isMulti
                            tabIndex={isOpen ? undefined : -1}
                        />
                        <CiLazySelect
                            ciType={'Program'}
                            label={t(`${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.Program_financuje_Projekt}`)}
                            name={ProgramProjektRelType}
                            setValue={setValue}
                            defaultValue={
                                programRelationFromUrl
                                    ? programRelationFromUrl.includes(JOIN_OPERATOR)
                                        ? programRelationFromUrl.split(JOIN_OPERATOR)
                                        : programRelationFromUrl
                                    : defaultFilterValues[ProgramProjektRelType] ?? ''
                            }
                            isMulti
                            tabIndex={isOpen ? undefined : -1}
                        />

                        <Input
                            id="name"
                            label={t(`filter.${entityName}.name`)}
                            placeholder={t(`filter.namePlaceholder`)}
                            {...register('Gen_Profil_nazov')}
                        />

                        <Input
                            id="metais-code"
                            label={t('filter.metaisCode.label')}
                            placeholder={codePrefix}
                            {...register('Gen_Profil_kod_metais')}
                        />
                        <MultiSelect
                            options={optionsForSelects}
                            setValue={setValue}
                            id="typ_sluzby"
                            label={investmentTypeConstraints?.name}
                            {...register('EA_Profil_Projekt_typ_investicie--eq')}
                            placeholder={t('customAttributeFilter.value.placeholderSelectMulti')}
                            onChange={(newValue) => {
                                setValue('EA_Profil_Projekt_typ_investicie--eq', Array.isArray(newValue) ? join(newValue, '+') : newValue)
                                setInvestmentType(newValue)
                            }}
                            value={investmentType}
                        />
                        <DynamicFilterAttributes
                            setValue={setValue}
                            defaults={defaultFilterValues}
                            filterData={{
                                attributeFilters: filter.attributeFilters ?? {},
                                metaAttributeFilters: filter.metaAttributeFilters ?? {},
                            }}
                            attributes={attributes as ExtendedAttribute[]}
                            attributeProfiles={attributeProfiles}
                            constraintsData={constraintsData}
                            isFocusable={isOpen}
                            ignoreInputNames={[MetainformationColumns.OWNER, 'EA_Profil_Projekt_typ_investicie']}
                        />
                    </>
                )
            }}
        />
    )
}

export const ProjektListPage: React.FC = () => {
    const { entityName: ciType } = useGetEntityParamsFromUrl()
    const { t } = useTranslation()

    const defaultFilterValues: ProjektFilterData = {
        Gen_Profil_nazov: '',
        Gen_Profil_kod_metais: '',
        [GestorProjektRelType]: '',
        [ProgramProjektRelType]: '',
    }

    return (
        <>
            <CiListContainer<CIFilterData>
                entityName={ciType ?? ''}
                defaultFilterValues={defaultFilterValues}
                ListComponent={(props) => (
                    <>
                        <BreadCrumbs
                            withWidthContainer
                            links={[
                                { label: t('breadcrumbs.home'), href: '/', icon: HomeIcon },
                                ...getCiHowToBreadCrumb(props.entityName, t),
                                {
                                    label: t('titles.ciList', { ci: props.ciTypeData?.name }) ?? '',
                                    href: `/ci/${ciType}`,
                                },
                            ]}
                        />
                        {!props.isLoading && <CiListWizard />}
                        <MainContentWrapper>
                            <ListWrapper {...props} customFilter={(filterProps) => <ProjektFilter {...filterProps} />} />
                        </MainContentWrapper>
                    </>
                )}
            />
        </>
    )
}
